import _ from 'lodash';
import moment from 'moment';
import store from '../redux/store';
import {
  showAddressModal,
  updateClosestShops,
  updateCustomerAddressCheck,
} from '../redux/actions';
import { getGeocodeByAddress, getGeocodeByPlaceId } from '../api/backend';
import { ORDER_TYPE_DELIVERY } from '../constants';
import { CUSTOMER_ADDRESS_CHECK_VALUES } from '../redux/reducers/componentReducer';
import { getAddressDetails, getAddressFormatted, parseAddress } from './map';
import { UPDATE_PENDING_ORDER } from '../redux/actions/types';
import { updateUserShopData } from '../api/users';
import { createPendingOrder, getPendingOrder } from '../api/pendingOrder';
import type { Address } from '../1_types/genericTypes';
import { getAvailableShops } from '../api/delivery';
import { handleJoiSchemaError } from './error';
import { logLowError, logMediumError } from './logger';
import {
  getPendingOrderCartResetPayload,
  sanitizePendingOrderAddress,
} from '../constants';

export const isCustomerAddressDefined = (customerAddress: any) => {
  const ignoreProperties = ['instruction', 'street2'];
  for (const key in customerAddress) {
    const isOtherPropertyThanIgnoredOnes = ignoreProperties.indexOf(key) === -1;
    const isDefined = customerAddress[key] != null;
    if (isOtherPropertyThanIgnoredOnes && isDefined) {
      return true;
    }
  }
  return false;
};

export const getCustomerAddress = async (
  customerId: string,
  withShopId = false,
): Promise<any> => {
  const pendingOrder =
    store.getState().pendingOrderReducer || (await getPendingOrder(customerId));
  const customerAddress = pendingOrder?.address;
  return withShopId
    ? { customerAddress, shopId: pendingOrder?.shopId }
    : customerAddress;
};

export const generateCustomerAddress = (address: any): any => {
  const newAddress = _.cloneDeep(address);
  newAddress.selected = true;
  return newAddress;
};

export const setUserShopIds = async (shopId: string, customerId: string) => {
  // Add shops to firebase
  try {
    const shopIds = store.getState()?.userReducer?.user?.shopIds;
    if (customerId && shopId) {
      const foundShopId = _.find(shopIds, (shopIdTemp) => {
        return shopIdTemp.shopId === shopId;
      });
      if (!foundShopId) {
        const shopIdData = {
          shopId,
          creationTime: moment().format(),
        };
        await updateUserShopData(customerId, shopId, shopIdData);
      }
    }
  } catch (error) {
    logLowError(`Error setting user shop ids ${error}`, { shopId, customerId });
  }
};

export const setUserPendingOrderAddressAndOrderTypeFirebase = async (
  customerId: string,
  shopId: string,
  address: any,
  orderType: string,
  pendingOrderOverrides: Record<string, any> = {},
): Promise<string> => {
  try {
    const { isUserAnonymous } = store.getState().userReducer as any;
    let message = '';
    const addressFromStore = store.getState().pendingOrderReducer?.address;
    const resolvedAddress = sanitizePendingOrderAddress(
      address === undefined ? addressFromStore : address
    );
    if (shopId && resolvedAddress && orderType) {
      const response = await createPendingOrder(customerId, {
        address: resolvedAddress,
        shopId,
        orderType,
        ...pendingOrderOverrides,
      });
      message = response.message || '';
    }
    if (!isUserAnonymous && shopId) {
      await setUserShopIds(shopId, customerId);
    }
    return message;
  } catch (error) {
    logMediumError(
      `Eerror setting user pending order ${handleJoiSchemaError(error)}`,
    );
    throw error;
  }
};

export const updateUserAddressAndOrderType = async (
  shopId: string,
  newAddress: any,
  orderType: string,
): Promise<string> => {
  const { user } = store.getState().userReducer;
  const { customerId } = user;
  const previousPendingOrder = store.getState().pendingOrderReducer;
  try {
    let resolvedAddress = newAddress;
    if (resolvedAddress === undefined) {
      resolvedAddress = previousPendingOrder?.address ?? null;
    }

    const shouldResetBasket =
      !!previousPendingOrder?.shopId &&
      !!shopId &&
      previousPendingOrder.shopId !== shopId;
    const pendingOrderOverrides = shouldResetBasket
      ? getPendingOrderCartResetPayload()
      : {};

    if (resolvedAddress) {
      resolvedAddress = generateCustomerAddress(resolvedAddress);
    }
    store.dispatch({
      type: UPDATE_PENDING_ORDER,
      payload: {
        shopId,
        address: resolvedAddress,
        orderType,
        ...pendingOrderOverrides,
      },
    });
    return await setUserPendingOrderAddressAndOrderTypeFirebase(
      customerId,
      shopId,
      resolvedAddress,
      orderType,
      pendingOrderOverrides,
    );
  } catch (error) {
    store.dispatch({
      type: UPDATE_PENDING_ORDER,
      payload: {
        shopId: previousPendingOrder?.shopId,
        address: previousPendingOrder?.address,
        orderType: previousPendingOrder?.orderType,
      },
    });
    logMediumError(
      `Error updating address and order type: ${handleJoiSchemaError(error)}`,
      { shopId, newAddress, orderType, customerId },
    );
    throw error;
  }
};

export const getDeliverableShops = async (
  address: Address | null | undefined,
  handleCatchError?: (error: unknown) => Promise<void>,
): Promise<any[]> => {
  try {
    const shops = await getAvailableShops(ORDER_TYPE_DELIVERY, address);
    return shops.map((shop: any) => ({
      ...shop,
      shopId: shop.id ?? shop.shopId,
    }));
  } catch (error) {
    if (handleCatchError) {
      await handleCatchError(error);
    }
    return [];
  }
};

export const checkIfCustomerAddressValid = async (
  customerAddress: any,
  orderType: string,
  shopId: string,
) => {
  const customerAddressCheck =
    store.getState().componentReducer?.customerAddressCheck;
  const shopIsLoaded = (store.getState().shopReducer as any)?.shopIsLoaded;
  if (
    customerAddressCheck === CUSTOMER_ADDRESS_CHECK_VALUES.VALID ||
    customerAddressCheck === CUSTOMER_ADDRESS_CHECK_VALUES.PENDING ||
    !shopIsLoaded ||
    !orderType
  ) {
    return;
  }
  if (!isCustomerAddressDefined(customerAddress)) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID),
    );
    return;
  }
  if (orderType !== ORDER_TYPE_DELIVERY) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID),
    );
    return;
  }
  store.dispatch(
    updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.PENDING),
  );

  const foundPlaces = await (customerAddress?.placeId
    ? getGeocodeByPlaceId(customerAddress.placeId)
    : getGeocodeByAddress(getAddressFormatted(customerAddress)));
  if (!foundPlaces?.length) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.INVALID),
    );
    store.dispatch(showAddressModal(true));
    return;
  }
  const firstFoundPlace = foundPlaces[0];
  const foundAddress = parseAddress(firstFoundPlace?.address_components);
  const { lat, lng } = firstFoundPlace.geometry.location;
  const { district, subLocality } = getAddressDetails([firstFoundPlace]);
  foundAddress.district = district;
  foundAddress.subLocality = subLocality;
  foundAddress.location = {
    latitude: lat,
    longitude: lng,
  };
  foundAddress.placeId = firstFoundPlace?.place_id;
  const deliverableShops = await getDeliverableShops(foundAddress);
  store.dispatch(updateClosestShops(deliverableShops));
  if (!deliverableShops?.length) {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.INVALID),
    );
    store.dispatch(showAddressModal(true));
  } else {
    store.dispatch(
      updateCustomerAddressCheck(CUSTOMER_ADDRESS_CHECK_VALUES.VALID),
    );
    const isDeliverableShopsHaveCurrentShopId = !!deliverableShops.find(
      (shop) => shop?.shopId === shopId,
    );
    if (!customerAddress?.placeId) {
      customerAddress.placeId = foundAddress.placeId;
    }
    const pendingOrderShopId = isDeliverableShopsHaveCurrentShopId
      ? shopId
      : deliverableShops[0]?.shopId;
    store.dispatch({
      type: UPDATE_PENDING_ORDER,
      payload: { shopId: pendingOrderShopId, address: customerAddress },
    });
  }
};
